import type * as ecModels from '../../../models'

/**
 * Configures the behavior of this plugin
 */
export interface Config<TTypeParams extends ecModels.TypeParams.Constraint> {
  /**
   * Determines the strategy for retrieving data: whether to use cache or make an API call.
   *
   * - `true`: The plugin will attempt to retrieve the entity from the cache. If the entity is
   *   not found, it defaults to making an API request.
   * - `false`: The plugin will always ignore the cache and make an API request to fetch the entity.
   * - `(entity: TTypeParams['entity']) => boolean`: A custom callback function that allows custom logic
   *   to dynamically decide whether to use the cache or the API based on the provided entity.
   *
   * @default true
   */
  useCacheInsteadOfApi: boolean | ((entity: TTypeParams['entity']) => boolean)
}
export namespace Config {
  /**
   * Returns a complete {@link Config} from the given {@link Options}
   */
  export function from<TTypeParams extends ecModels.TypeParams.Constraint>(
    config: Readonly<Config.Options<TTypeParams>> | undefined,
  ): Config<TTypeParams> {
    return {
      ...config,
      useCacheInsteadOfApi: config?.useCacheInsteadOfApi ?? true,
    }
  }

  /**
   * More flexible version of {@link Config} which allows some properties to be
   * omitted
   *
   * @see {@link Config.from}
   */
  export type Options<TTypeParams extends ecModels.TypeParams.Constraint> =
    Partial<Config<TTypeParams>>
}
